import { defineStore } from 'pinia';
import { vuexStoreWriteMutation } from '@/services/iframe';
import { resolveLegacyNamespace } from '@/stores/legacyBridge';
import router from '@/router/router';

interface PreferencesState {
  /** Last selected channel per bot: {[botId]: channelId} */
  lastChannelId: Record<string, string | null>;
  /** Last viewed intent per bot: {[botId]: intentName} */
  lastIntentName: Record<string, string | null>;
  /** Desktop sidebar width in rem (`null` = default). New tabs start with it; open tabs don't sync. */
  sidebarWidth: number | null;
  /** Desktop sidebar expanded/collapsed (`null` = fall back to the legacy `sidebar_state` cookie). */
  sidebarOpen: boolean | null;
  /**
   * Expanded (true) / collapsed (false) HubBox sections per page: `{ [namespace]: { [boxId]: open } }`,
   * e.g. `intents` / `forms` for their details sidebars. See `components/hub/hubBoxPersistence.ts`.
   */
  sidebarBoxes: Record<string, Record<string, boolean>>;
}

/**
 * Pinia conversion of legacy vuex module `src/store/modules/preferences.ts` (namespace
 * `preferences`). Persisted to `localStorage` (full state) — matches the legacy `vuexLocal`
 * reducer's `preferences: state.preferences` entry in `src/store/index.ts`.
 *
 * Parent-frame reflection: `setLastChannelId` and `setLastIntentName` call
 * `vuexStoreWriteMutation('preferences/<legacyMutationName>', ...)` AFTER applying the change,
 * mirroring the legacy `vuexStore.subscribe` callback in `src/store/index.ts` that reflected every
 * `preferences/*` mutation to the parent (old) hub over postMessage.
 *
 * Collisions & renames (see
 * `/private/tmp/claude-501/-Users-haslien-Documents-GitHub-moin-hub/23091b27-f9d9-4b85-91e6-ff6cd1c12710/scratchpad/pinia-migration/RENAMES.md`):
 * - Legacy had BOTH a getter and an action named `lastSelectedChannelId`. The getter keeps the
 *   name; the action is renamed `lastSelectedChannelIdAction`.
 * - `setLastChannelId` existed as BOTH an action (which just committed the mutation) and a
 *   mutation of the same name — merged into a single action.
 * - `setLastIntentName` only existed as a mutation (no action) — becomes a same-named action.
 */
export const usePreferencesStore = defineStore('preferences', {
  state: (): PreferencesState => ({
    // {[botId]: channelId}
    lastChannelId: {},
    // {[botId]: intentName}
    lastIntentName: {},
    sidebarWidth: null,
    sidebarOpen: null,
    sidebarBoxes: {},
  }),
  getters: {
    /**
     * Return the channel ID from the URL query string
     */
    queryChannelId(): string | null {
      return new URLSearchParams(window.location.search)?.get('channel') || null;
    },
    /**
     * Resolves which channel you are going to want to be using, for the currently selected bot.
     * Prefers URL query's channel if set, otherwise it uses the last known selected channel, and
     * if not, falls back to the first/default one. Validates that the channel returned also
     * exists in the bot.
     */
    suggestedChannelId(): string | null {
      // `bots` hasn't been converted to Pinia yet (Phase 1 only covers `app`, `preferences`,
      // `notifications`). Resolve it through the legacy bridge registry instead of a direct
      // `useBotsStore()` import so this file type-checks and behaves correctly today, and starts
      // working for real the moment a later phase registers `bots` there — no change needed here.
      // TODO: Phase 2 replaces this with `useBotsStore()` once `src/stores/bots.ts` exists.
      const botsStore = resolveLegacyNamespace('bots');
      if (!botsStore) return null;

      const botId = botsStore.currentBotId;
      if (botId === null) return null;

      const channelExists = (channelId: string) => {
        return botsStore.currentChannels.some(
          (ch: any) => !ch.blocked && ch.channelId === channelId,
        );
      };

      // Read the route reactively. `window.location` is not reactive, so with it this cached getter
      // kept returning the channel of an earlier URL after in-app navigation, and components that
      // fall back to the suggested channel (e.g. ChannelSelection on mount) switched to that stale one.
      const queryChannel = router.currentRoute.value.query.channel;
      let channelId = (Array.isArray(queryChannel) ? queryChannel[0] : queryChannel) || null;
      if (channelId && channelExists(channelId)) return channelId;

      channelId = this.lastChannelId[botId];
      if (channelId && channelExists(channelId)) return channelId;

      // Fallback: First channel that is not blocked
      const bot = botsStore.getBot(botId);
      const ch = bot?.channels?.find((ch: any) => !ch.blocked);
      if (ch) return ch.channelId;

      // If all channels blocked, use the first channel regardless,
      // or `null` if no channels at all.
      return bot?.channels?.[0]?.channelId ?? null;
    },
    /**
     * Returns a getter function that will return last channel ID used for a given bot ID
     */
    lastSelectedChannelId(): (botId: string) => string | null {
      return (botId: string) => this.lastChannelId[botId] ?? null;
    },
    setQueryChannelId(): (channelId: string | null) => void {
      return (channelId: string | null) => {
        // First, remove all params
        const url = new URL(window.location.href);
        while (url.searchParams.has('channel')) url.searchParams.delete('channel');
        // If channel ID is not null, add it
        if (channelId !== null) url.searchParams.append('channel', channelId);

        window.history.replaceState(window.history.state, '', url.toString());
      };
    },
    /**
     * A function to get the last intent name for a bot
     */
    getLastIntentName(): (botId: string) => string | null {
      return (botId: string) => this.lastIntentName[botId] ?? null;
    },
  },
  actions: {
    /**
     * Renamed from legacy action `lastSelectedChannelId` (collides with the getter of the same
     * name — the getter keeps the name). Get the last selected channel for a bot by ID.
     */
    lastSelectedChannelIdAction(botId: string): string | null {
      return this.lastChannelId[botId] ?? null;
    },
    /**
     * Merged from legacy action `setLastChannelId` (which only committed the mutation of the
     * same name) + legacy mutation `setLastChannelId`.
     *
     * Exists so you can call this from other stores to update this preference.
     * Ideally, you should set the channel ID in `bot/setCurrentChannelId` instead.
     * It will be propagated here automatically.
     * @private
     */
    async setLastChannelId(payload: { botId: string; channelId: string | null }) {
      this.lastChannelId = { ...this.lastChannelId, [payload.botId]: payload.channelId };
      vuexStoreWriteMutation('preferences/setLastChannelId', JSON.parse(JSON.stringify(payload)));
    },
    /**
     * Converted from legacy mutation `setLastIntentName` (no matching legacy action existed).
     * Sets which intent name the user last viewed in a bot.
     */
    setLastIntentName(payload: { botId: string; intentName: string }) {
      this.lastIntentName = { ...this.lastIntentName, [payload.botId]: payload.intentName };
      vuexStoreWriteMutation(
        'preferences/setLastIntentName',
        JSON.parse(JSON.stringify(payload)),
      );
    },
    /** Hub-only preference (not reflected to the legacy parent frame). */
    setSidebarWidth(rem: number) {
      this.sidebarWidth = Math.round(rem * 1000) / 1000;
    },
    /** Hub-only preference (not reflected to the legacy parent frame). */
    setSidebarOpen(open: boolean) {
      this.sidebarOpen = open;
    },
    /** Hub-only preference: remember whether a HubBox section is expanded on a page. */
    setSidebarBoxOpen(namespace: string, boxId: string, open: boolean) {
      this.sidebarBoxes = {
        ...this.sidebarBoxes,
        [namespace]: { ...this.sidebarBoxes[namespace], [boxId]: open },
      };
    },
    /**
     * Updates the web URL according to the input.
     * Removes redundant channel query, and adds if relevant
     *
     * Kept as dead-but-intentionally-preserved code (early `return`), exactly as in the legacy
     * module — "Not in use, but kept around for a bit longer just in case."
     */
    updateQueryParams({
      botId: _botId,
      channelId,
    }: {
      botId: string;
      channelId: string | null;
    }) {
      return; // Not in use, but kept around for a bit longer just in case.

       
      const botsStore = resolveLegacyNamespace('bots'); // TODO: Phase 2 replaces this with useBotsStore()
      const channels: any = botsStore?.currentChannels;
      const url = new URL(window.location.href);
      const queryChId = url.searchParams.get('channel');

      // Usually we shouldn't need these checks if done right, but done just in case.
      // Careful handling: if channelId is null, replace with first channel
      if (channelId === null) channelId = channels?.[0]?.channelId;
      // Careful handling: if not explicitly defined, read from URL query
      if (!channelId) channelId = queryChId;
      // Careful handling: You can have multiple identical queries. Remove all but one.
      while (url.searchParams.has('channel')) url.searchParams.delete('channel');
      url.searchParams.append('channel', queryChId as any);

      // 0 or 1 channels || first channel selected: Remove redundant channel query parameter
      if (channels.length <= 1 || channels?.[0]?.channelId === channelId) {
        url.searchParams.delete('channel');

        // Stop processing
        return window.history.replaceState(window.history.state, '', url.toString());
      }

      // ... by now channelId cannot possibly be the default channel ID.

      // Channel provided, but it's not matching the URL query (or no query set)? Change it
      if (queryChId !== channelId) {
        url.searchParams.delete('channel');
        url.searchParams.append('channel', channelId as any);
      }
      // 2+ channels: Add current channel to query parameters, if it is not the default one
      else if (channels.length > 1) {
        const chId = this.suggestedChannelId;

        // Channel at index 0 is the default channel.
        if (chId !== channels[0]?.channelId) {
          url.searchParams.delete('channel');
          url.searchParams.append('channel', chId as any);
        }
      }

      // Update params with the final state
      window.history.replaceState(window.history.state, '', url.toString());
    },
  },
  vuexPersist: {
    local: true,
  },
});
